import { useEffect, useState } from "react";

/**
 * Permisos por módulo (espejo de backend/apps/core/permisos.py).
 *
 * El superadministrador asigna a cada usuario, contador u operador de agua
 * qué opciones del menú puede ver. La lista llega en el perfil
 * (usuario_condominio.permisos) y se guarda en localStorage["permisos"].
 *
 * Esto es navegación, no seguridad: el backend valida cada endpoint igual.
 */

const EVENTO = "permisos:actualizados";

export const CLAVES_AGUA = ["agua_captura", "agua_historial", "agua_resumen"];

function leer() {
  try {
    const lista = JSON.parse(localStorage.getItem("permisos") || "[]");
    return Array.isArray(lista) ? lista : [];
  } catch {
    return [];
  }
}

/** true si el usuario de la sesión puede ver la opción `clave`. */
export function puede(clave) {
  if (localStorage.getItem("is_superuser") === "true") return true;
  const rol = localStorage.getItem("rol") || "USUARIO";
  if (rol === "RESIDENTE") return true;
  const permisos = leer();
  if (clave === "agua") return CLAVES_AGUA.some(c => permisos.includes(c));
  if (rol === "AGUA" && !CLAVES_AGUA.includes(clave)) return false;
  return permisos.includes(clave);
}

/** Guarda la lista nueva y avisa a quien esté mostrando el menú. */
export function guardarPermisos(lista) {
  try {
    localStorage.setItem("permisos", JSON.stringify(Array.isArray(lista) ? lista : []));
  } catch { /* noop */ }
  window.dispatchEvent(new Event(EVENTO));
}

/** Devuelve `puede` y vuelve a renderizar cuando cambian los permisos. */
export function usePermisos() {
  const [, setVersion] = useState(0);
  useEffect(() => {
    const refrescar = () => setVersion(v => v + 1);
    window.addEventListener(EVENTO, refrescar);
    return () => window.removeEventListener(EVENTO, refrescar);
  }, []);
  return puede;
}

// Ruta → clave. Las más específicas primero: se toma la primera que calce.
const RUTAS = [
  ["/residentes",                  "residentes"],
  ["/proveedores",                 "proveedores"],
  ["/finanzas/asientos",           "asientos"],
  ["/finanzas/catalogo",           "catalogo"],
  ["/finanzas/ingresos",           "ingresos"],
  ["/finanzas/gastos",             "gastos"],
  ["/finanzas/presupuesto",        "presupuesto"],
  ["/finanzas/bancos/cuentas",     "bancos_cuentas"],
  ["/finanzas/bancos/movimientos", "bancos_movimientos"],
  ["/finanzas/conciliacion",       "conciliacion"],
  ["/finanzas/traslados",          "traslados"],
  ["/finanzas/prestamos",          "prestamos"],
  ["/finanzas/cxp/facturas",       "cxp_facturas"],
  ["/finanzas/cxp/recibos",        "cxp_recibos"],
  ["/finanzas/cxp/notas-",         "cxp_notas"],
  ["/finanzas/cxp",                "cxp_dashboard"],
  ["/finanzas/cxc",                "cxc_dashboard"],
  ["/finanzas/facturas",           "cxc_facturas"],
  ["/finanzas/recibos",            "cxc_recibos"],
  ["/finanzas/notas-",             "cxc_notas"],
  ["/finanzas/rubros",             "cxc_rubros"],
  ["/finanzas/estados",            "estados_financieros"],
  ["/finanzas/balance-",           "estados_financieros"],
  ["/finanzas/estado-",            "estados_financieros"],
  ["/agua/historial",              "agua_historial"],
  ["/agua/resumen",                "agua_resumen"],
  ["/portal/admin/residentes",     "portal_residentes"],
  ["/portal/admin/areas",          "portal_areas"],
  ["/portal/admin/reservaciones",  "portal_reservaciones"],
  ["/portal/admin/avisos-pago",    "portal_avisos_pago"],
  ["/portal/admin/avisos",         "portal_avisos"],
];

/** Clave que protege una ruta, o null si la ruta es libre (inicio, ajustes...). */
export function claveDeRuta(pathname, search = "") {
  if (pathname === "/agua") return "agua_captura";
  if (pathname.startsWith("/finanzas/informes")) {
    const tipo = new URLSearchParams(search).get("tipo");
    return tipo === "cxp" ? "cxp_informes" : "cxc_informes";
  }
  const hit = RUTAS.find(([prefijo]) => pathname.startsWith(prefijo));
  return hit ? hit[1] : null;
}

/** Primera pestaña de Agua que el usuario puede abrir (o null). */
export function primeraRutaAgua() {
  if (puede("agua_captura"))   return "/agua";
  if (puede("agua_historial")) return "/agua/historial";
  if (puede("agua_resumen"))   return "/agua/resumen";
  return null;
}
